import { useSettingsStore } from '~/stores/settings';
import type { SettingsUpdatePayload } from '~/types/settings';
import RequestService from './request';

interface GeneralSettingsRequest {
    ['branding.name']: string;
    ['misc.graphs_show_max_resource_limit']: boolean;
    ['misc.required_2fa']: number;
    ['misc.graphs_show_max_resource_limit']: boolean;
    ['misc.hide_admin_audit_logs_from_users']: boolean;
    ['default_locale']: string;
}

interface NodesSettingsRequest {
    ['deployment.strategy']: string;
    ['misc.databases_allow_random']: boolean;
}

interface EmbedSettingsRequest {
    ['og.site_name']: string;
    ['og.title']: string;
    ['og.description']: string;
    ['og.color']: string;
    ['og.image']: string;
}

interface JSSettingsRequest {
    js: string;
}

interface CSSSettingsRequest {
    css: string;
}

interface SSOSettingsRequest {
    ['whmcs.enabled']: boolean;
    ['whmcs.button_text']: string;
    ['whmcs.url']: string;
    ['whmcs.client_id']: string;
    ['whmcs.client_secret']: string;
    ['whmcs.api_id']: string;
    ['whmcs.api_secret']: string;
}

interface SMTPSettingsRequest {
    ['smtp.enabled']: boolean;
    ['smtp.host']: string;
    ['smtp.port']: number;
    ['smtp.encryption']: string;
    ['smtp.username']: string;
    ['smtp.password']: string;
    ['smtp.from_address']: string;
    ['smtp.from_name']: string;
}

interface FirewallProviderEntry {
    type: string;
    prefixes: string[];
    url?: string | null;
    api_key?: string | null;
}

interface FirewallSettingsRequest {
    providers: FirewallProviderEntry[];
}

interface AssetRequest {
    asset?: File;
}

class SettingsService {
    fetchAsset(url: string) {
        // Avoid using axios due to CORS
        return fetch(url).then((res) => res.text());
    }

    updateGeneral(data: GeneralSettingsRequest) {
        // TODO: check if lang should change
        return RequestService.post('/settings/general', data).then(() =>
            useSettingsStore().updateSettings({
                default_locale: data.default_locale,
                branding: {
                    name: data['branding.name'],
                },
                misc: {
                    graphs_show_max_resource_limit: data['misc.graphs_show_max_resource_limit'],
                    required_2fa: data['misc.required_2fa'],
                },
            }),
        );
    }

    updateNodes(data: NodesSettingsRequest) {
        return RequestService.post('/settings/nodes', data).then(() =>
            useSettingsStore().updateSettings({
                deployment: {
                    strategy: data['deployment.strategy'],
                },
                misc: {
                    databases_allow_random: data['misc.databases_allow_random'],
                },
            } satisfies SettingsUpdatePayload),
        );
    }

    updateEmbed(data: EmbedSettingsRequest) {
        return RequestService.post('/settings/embed', data).then(() =>
            useSettingsStore().updateSettings({
                og: {
                    site_name: data['og.site_name'],
                    title: data['og.title'],
                    description: data['og.description'],
                    color: data['og.color'],
                    image: data['og.image'],
                },
            }),
        );
    }

    updateJS(data: JSSettingsRequest) {
        return RequestService.post('/settings/js', data).then(({ url }) => useSettingsStore().updateSettings({ injector: { js: url } }));
    }

    updateCSS(data: CSSSettingsRequest) {
        return RequestService.post('/settings/css', data).then(({ url }) => useSettingsStore().updateSettings({ injector: { css: url } }));
    }

    updateSSO(data: SSOSettingsRequest) {
        return RequestService.post('/settings/sso', data).then(() =>
            useSettingsStore().updateSettings({
                whmcs: {
                    enabled: data['whmcs.enabled'],
                    button_text: data['whmcs.button_text'],
                    url: data['whmcs.url'],
                    client_id: data['whmcs.client_id'],
                    client_secret: data['whmcs.client_secret'],
                    api_id: data['whmcs.api_id'],
                    api_secret: data['whmcs.api_secret'],
                },
            }),
        );
    }

    updateSMTP(data: SMTPSettingsRequest) {
        return RequestService.post('/settings/smtp', data).then(() =>
            useSettingsStore().updateSettings({
                smtp: {
                    enabled: data['smtp.enabled'],
                    host: data['smtp.host'],
                    port: data['smtp.port'],
                    encryption: data['smtp.encryption'],
                    username: data['smtp.username'],
                    password: data['smtp.password'],
                    from_address: data['smtp.from_address'],
                    from_name: data['smtp.from_name'],
                },
            }),
        );
    }

    updateFirewall(data: FirewallSettingsRequest) {
        return RequestService.post('/settings/firewall', data).then(() => useSettingsStore().updateSettings({ firewall: data }));
    }

    private assetToFormData(data: AssetRequest) {
        const formData = new FormData();
        if (data.asset) formData.append('asset', data.asset);
        return formData;
    }

    uploadLoginLogo(data: AssetRequest) {
        return RequestService.post('/settings/branding/login_logo', this.assetToFormData(data), {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(({ url }) =>
            useSettingsStore().updateSettings({
                branding: { login_logo: url },
            } satisfies SettingsUpdatePayload),
        );
    }

    uploadFavicon(data: AssetRequest) {
        return RequestService.post('/settings/branding/favicon', this.assetToFormData(data), {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(({ url }) =>
            useSettingsStore().updateSettings({
                branding: { favicon: url },
            } satisfies SettingsUpdatePayload),
        );
    }

    uploadLogo(data: AssetRequest) {
        return RequestService.post('/settings/branding/logo', this.assetToFormData(data), {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then(({ url }) =>
            useSettingsStore().updateSettings({
                branding: { logo: url },
            } satisfies SettingsUpdatePayload),
        );
    }
}

export default new SettingsService();
